<template>
  <Teleport :to="teleportTo ?? 'body'" :disabled="inline">
    <div v-if="modelValue"
class="modal-overlay"
:class="{ 'modal-overlay--inline': inline }"
:data-theme="light ? 'light' : undefined"
@mousedown="!inline && handleOverlayMouseDown($event)"
@mouseup="!inline && handleOverlayMouseUp($event)">
    <div
      class="modal-content"
      :role="inline ? undefined : 'dialog'"
      :aria-modal="inline ? undefined : true"
      :aria-label="$t('auth.passwordRecovery')"
      @keydown.esc.stop="close"
    >
      <button v-if="!inline" class="modal-close" @click="close">
        <i class="zmdi zmdi-close" />
      </button>
      
      <h2 class="modal-title">{{ $t('auth.passwordRecovery') }}</h2>

      <div class="captcha-hidden-container">
        <CaptchaWidget
          ref="captchaRef"
          @verified="onCaptchaVerified"
          @expired="onCaptchaExpired"
          @error="onCaptchaError"
          @cancelled="onCaptchaCancelled"
        />
      </div>

      <TelegramDeliveryRecovery
        v-if="step === 'delivery-recovery'"
        :recovery="telegramRecovery"
        :busy="isLoading || isResending"
        :error-message="errorMessage"
        @retry="retryTelegramDelivery"
        @back="goBack(1)"
      />
      <!-- Спринт 14: пока канал не выбран, экрана ввода кода нет -->
      <template v-else-if="choiceChannels.length">
        <OtpChannelChoice
          :channels="choiceChannels"
          :busy="isLoading || isResending"
          @pick="handleChoicePick"
        />
        <p v-if="errorMessage" class="form-error" v-html="errorMessage" />
      </template>

      <!-- Step 1: Email Input -->
      <form v-else-if="step === 1" @submit.prevent="handleSendCode" class="auth-form">
        <p class="modal-subtitle">
          {{ $t('auth.passwordRecoverySubtitle') }}
        </p>

        <div class="form-group">
          <label class="form-label">{{ $t('auth.email') }}</label>
          <BaseInput bare
            v-model="form.email" 
            type="email" 
            class="form-input" 
            :placeholder="$t('auth.enterEmail')"
            required
          />
        </div>

        <p v-if="errorMessage" class="form-error" v-html="errorMessage" />

        <BaseButton
          monochrome
          type="submit"
          class="btn btn-primary btn-block"
          :disabled="isLoading || resendCooldown > 0 || codeThrottled">
          <template v-if="isLoading">{{ $t('auth.sending') }}</template>
          <template v-else-if="resendCooldown > 0">{{ $t('auth.resendCodeIn', { seconds: resendCooldown }) }}</template>
          <template v-else>{{ $t('auth.sendCode') }}</template>
        </BaseButton>

        <div class="auth-footer">
          <p><a href="#" @click.prevent="switchToLogin">{{ $t('auth.backToLogin') }}</a></p>
        </div>
      </form>

      <!-- Step 2: Code Verification -->
      <form v-else-if="step === 2" @submit.prevent="handleConfirmCode" class="auth-form">
        <div class="success-icon">
          <i class="zmdi zmdi-email" />
        </div>
        <p class="modal-subtitle">
          {{ $t('auth.codeSent') }} 
          <strong v-if="codeReceiver === 'telegram'">Telegram</strong>
          <strong v-else>{{ receiverAddress || form.email }}</strong>.
        </p>

        <div class="form-group">
          <div v-if="sessionNumber" class="session-number">
            {{ $t('auth.session', { number: sessionNumber }) }}
          </div>
          <label class="form-label">{{ $t('auth.approvalConfirmationCode') }}</label>
          <div class="code-digits">
            <input
              v-for="(_, index) in 6"
              :key="index"
              ref="codeInputs"
              type="text"
              inputmode="numeric"
              maxlength="1"
              v-model="codeDigits[index]"
              @input="handleCodeInput($event, index)"
              @keydown.delete="handleCodeDelete(index)"
              @paste="handleCodePaste"
              class="code-digit"
              autocomplete="one-time-code"
              :disabled="codeBurned"
            />
          </div>
        </div>

        <p v-if="errorMessage" class="form-error" v-html="errorMessage" />

        <!-- 32: код сожжён, ввод бесполезен до нового кода -->
        <BaseButton
          monochrome
          variant="ghost"
          v-if="codeBurned && !codeThrottled"
          type="button"
          class="btn btn-outline btn-block"
          data-test="request-new-code"
          :disabled="isResending"
          @click="requestNewCode"
        >
          {{ $t('auth.codeLimits.requestNew') }}
        </BaseButton>

        <div v-if="!codeBurned" class="resend-link">
          <a 
            href="#" 
            class="resend-code"
            @click.prevent="handleResendCode" 
            :class="{ disabled: isResending || resendCooldown > 0 || codeThrottled }"
          >
            <template v-if="isResending">{{ $t('auth.sending') }}</template>
            <template v-else-if="resendCooldown > 0">{{ $t('auth.resendCodeIn', { seconds: resendCooldown }) }}</template>
            <template v-else>{{ $t('auth.resendCode') }}</template>
          </a>
        </div>

        <div class="step-actions">
          <BaseButton
            monochrome
            variant="ghost"
            type="button"
class="btn btn-outline"
@click="goBack(1)"
:disabled="isLoading">
            {{ $t('auth.back') }}
          </BaseButton>
          <BaseButton
            monochrome
            type="submit"
            class="btn btn-primary"
            :disabled="isLoading || !isCodeComplete || codeBurned">
            {{ isLoading ? $t('auth.verifying') : $t('auth.confirm') }}
          </BaseButton>
        </div>
      </form>

      <!-- OTP-R6c: у аккаунта с приложением-аутентификатором код из письма — только первый фактор -->
      <form
        v-else-if="step === 'totp'"
        data-test="recovery-totp"
        @submit.prevent="handleTotpSubmit"
        class="auth-form"
      >
        <p class="modal-subtitle">{{ $t('auth.enterTotpCode') }}</p>

        <div class="form-group">
          <label class="form-label">{{ $t('auth.approvalConfirmationCode') }}</label>
          <div class="code-digits">
            <input
              v-for="(_, index) in 6"
              :key="index"
              ref="totpInputs"
              type="text"
              inputmode="numeric"
              maxlength="1"
              v-model="totpDigits[index]"
              @input="handleTotpInput($event, index)"
              @paste.prevent="handleTotpPaste"
              class="code-digit"
              autocomplete="one-time-code"
            />
          </div>
        </div>

        <p v-if="errorMessage" class="form-error" v-html="errorMessage" />

        <div class="step-actions">
          <BaseButton
            monochrome
            variant="ghost"
            type="button"
class="btn btn-outline"
@click="goBack(1)"
:disabled="isLoading">
            {{ $t('auth.back') }}
          </BaseButton>
          <BaseButton
            monochrome
            type="submit"
            class="btn btn-primary"
            :disabled="isLoading || !isTotpComplete">
            {{ isLoading ? $t('auth.verifying') : $t('auth.confirm') }}
          </BaseButton>
        </div>
      </form>

      <!-- Step 3: New Password -->
      <form v-else-if="step === 3" @submit.prevent="handleChangePassword" class="auth-form">
        <p class="modal-subtitle">
          {{ $t('auth.newPasswordSubtitle') }}
        </p>

        <div class="form-group">
          <label class="form-label">{{ $t('auth.newPassword') }}</label>
          <div class="password-input-wrapper">
            <BaseInput bare
              v-model="form.password" 
              :type="showPassword ? 'text' : 'password'" 
              class="form-input" 
              :placeholder="$t('auth.enterPassword')"
              required
            />
            <button type="button"
class="toggle-password"
tabindex="-1"
@click="showPassword = !showPassword">
              <i :class="showPassword ? 'zmdi zmdi-eye-off' : 'zmdi zmdi-eye'" />
            </button>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">{{ $t('auth.repeatPassword') }}</label>
          <div class="password-input-wrapper">
            <BaseInput bare
              v-model="form.passwordConfirm" 
              :type="showPassword ? 'text' : 'password'" 
              class="form-input" 
              :class="{ 'input-error': passwordMismatch }"
              :placeholder="$t('auth.repeatPassword')"
              required
            />
          </div>
          <p v-if="passwordMismatch" class="field-error">{{ $t('auth.passwordsMismatch') }}</p>
        </div>

        <!-- Password Requirements -->
        <div class="password-requirements">
          <div class="requirement" :class="{ met: hasMinLength }">
            <i :class="hasMinLength ? 'zmdi zmdi-check' : 'zmdi zmdi-close'" />
            {{ $t('auth.passwordRequirements.minLength') }}
          </div>
          <div class="requirement" :class="{ met: hasUppercase }">
            <i :class="hasUppercase ? 'zmdi zmdi-check' : 'zmdi zmdi-close'" />
            {{ $t('auth.passwordRequirements.uppercase') }}
          </div>
          <div class="requirement" :class="{ met: hasLowercase }">
            <i :class="hasLowercase ? 'zmdi zmdi-check' : 'zmdi zmdi-close'" />
            {{ $t('auth.passwordRequirements.lowercase') }}
          </div>
          <div class="requirement" :class="{ met: hasNumber }">
            <i :class="hasNumber ? 'zmdi zmdi-check' : 'zmdi zmdi-close'" />
            {{ $t('auth.passwordRequirements.digit') }}
          </div>
          <div class="requirement" :class="{ met: hasSpecial }">
            <i :class="hasSpecial ? 'zmdi zmdi-check' : 'zmdi zmdi-close'" />
            {{ $t('auth.passwordRequirements.special') }}
          </div>
        </div>

        <p v-if="errorMessage" class="form-error" v-html="errorMessage" />

        <BaseButton monochrome
          type="submit" 
          class="btn btn-primary btn-block" 
          :disabled="isLoading || !isPasswordValid || form.password !== form.passwordConfirm"
        >
          {{ isLoading ? $t('auth.saving') : $t('auth.changePassword') }}
        </BaseButton>
      </form>
    </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import BaseButton from '@/shared/components/base/BaseButton.vue'
import BaseInput from '@/shared/components/base/BaseInput.vue'
import { computed, nextTick, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ProfileApi } from '@/modules/profile/api/profileApi'
import type { OtpChoice, OtpChoiceChannel } from '@/modules/profile/api/profileApi'
import OtpChannelChoice from '@/modules/auth/components/OtpChannelChoice.vue'
import TelegramDeliveryRecovery from './TelegramDeliveryRecovery.vue'
import { isTelegramDeliveryFailure, parseTelegramRecovery } from '../utils/telegramDeliveryRecovery'
import type { TelegramRecovery } from '../utils/telegramDeliveryRecovery'
import { getErrorMessage } from '@/shared/utils/apiErrorParser'
import { useModalOverlay } from '@/composables/useModalOverlay'
import { isCaptchaSessionExpiredError, requestCaptchaToken } from '@/shared/utils/captchaRetryService'
import CaptchaWidget from '@/shared/components/CaptchaWidget.vue'
import { AuthRequestCancelled, assertCurrentAuthRequest } from '@/shared/api/authRequestGuard'
import { httpClient } from '@/shared/api/httpClient'
import { useProfileStore } from '@/modules/profile/store/profileStore'
import { useCodeLimit } from '@/composables/useCodeLimit'

const { t } = useI18n()

const props = defineProps<{
  teleportTo?: HTMLElement
  modelValue: boolean
  light?: boolean
  inline?: boolean
  initialEmail?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'switch-to-login'): void
}>()

const profileStore = useProfileStore()
const { isBurned: codeBurned, isThrottled: codeThrottled, apply: applyCodeLimit, reset: resetCodeLimit } = useCodeLimit()

let generation = 0
// OTP-R6c: pending-токен восстановления принадлежит своей попытке. При отказе от неё
// удаляем только его и только если в хранилище всё ещё он, а не чужой/полный токен.
let pendingRecovery: { token: string | null } | null = null
// OTP-R3: реактивный признак нужен store — по нему он синхронно снимает busy отменённой попытки.
const approvalRequestActive = ref(false)
const telegramRecovery = ref<TelegramRecovery | null>(null)
const invalidateRequests = () => {
  generation++
  telegramRecovery.value = null
  pendingCaptchaAction.value = null
  isLoading.value = false
  isResending.value = false
  approvalRequestActive.value = false
  const pending = pendingRecovery
  pendingRecovery = null
  if (pending?.token && httpClient.getToken() === pending.token) httpClient.clearToken()
}
const step = ref<1 | 2 | 3 | 'totp' | 'delivery-recovery'>(1)
const isLoading = ref(false)
const isResending = ref(false)
const errorMessage = ref('')
const showPassword = ref(false)
const codeReceiver = ref<'email' | 'telegram'>('email')
// Спринт 14: бэкенд вернул фазу выбора — код не отправлен, ждём ответа.
const choiceChannels = ref<string[]>([])
const chosenChannel = ref<OtpChoiceChannel>()
const receiverAddress = ref('')
const recoveryToken = ref('')
const sessionNumber = ref('')

// Captcha state
const captchaRef = ref<InstanceType<typeof CaptchaWidget> | null>(null)
const captchaToken = ref('')
const captchaSession = ref('')
const captchaSessionExpiry = ref(0)

// Track which action triggered captcha
type CaptchaAction = 'send_code' | null
const pendingCaptchaAction = ref<CaptchaAction>(null)

// Resend cooldown timer
const RESEND_COOLDOWN_SECONDS = 60
const resendCooldown = ref(0)
let cooldownIntervalId: ReturnType<typeof setInterval> | null = null

const startResendCooldown = () => {
  resendCooldown.value = RESEND_COOLDOWN_SECONDS
  if (cooldownIntervalId) {
    clearInterval(cooldownIntervalId)
  }
  cooldownIntervalId = setInterval(() => {
    resendCooldown.value--
    if (resendCooldown.value <= 0) {
      if (cooldownIntervalId) {
        clearInterval(cooldownIntervalId)
        cooldownIntervalId = null
      }
    }
  }, 1000)
}

onBeforeUnmount(() => {
  invalidateRequests()
  if (cooldownIntervalId) {
    clearInterval(cooldownIntervalId)
  }
})

const onCaptchaVerified = (token: string) => {
  captchaToken.value = token
  
  const action = pendingCaptchaAction.value
  pendingCaptchaAction.value = null // Reset after use

  if (action !== 'send_code') {
    console.log('[GonkaGame] Password recovery captcha verified silently, token stored')
    return
  }
  
  // After captcha verified, perform the actual send code
  performSendCode()
}

const onCaptchaExpired = () => {
  captchaToken.value = ''
}

const onCaptchaCancelled = () => {
  captchaToken.value = ''
  pendingCaptchaAction.value = null
}

const onCaptchaError = () => {
  onCaptchaCancelled()
  errorMessage.value = t('auth.captchaError')
}

const form = reactive({
  email: '',
  password: '',
  passwordConfirm: ''
})

const codeDigits = reactive<string[]>(new Array(6).fill(''))
const totpDigits = reactive<string[]>(new Array(6).fill(''))
const totpInputs = ref<HTMLInputElement[]>([])
const isTotpComplete = computed(() => totpDigits.every(d => d.length === 1))
const codeInputs = ref<HTMLInputElement[]>([])
const isCodeComplete = computed(() => codeDigits.every(d => d.length === 1))
const getCodeValue = () => codeDigits.join('')
const hasMinLength = computed(() => form.password.length >= 8)
const hasUppercase = computed(() => /[A-Z]/.test(form.password))
const hasLowercase = computed(() => /[a-z]/.test(form.password))
const hasNumber = computed(() => /[0-9]/.test(form.password))
const hasSpecial = computed(() => /[^A-Za-z0-9]/.test(form.password))
const isPasswordValid = computed(() => 
  hasMinLength.value && hasUppercase.value && hasLowercase.value && hasNumber.value && hasSpecial.value
)
const passwordMismatch = computed(() => 
  form.passwordConfirm.length > 0 && form.password !== form.passwordConfirm
)

const close = () => {
  emit('update:modelValue', false)
  resetState()
}

// Modal overlay click handling (prevents accidental close on text selection)
const { handleOverlayMouseDown, handleOverlayMouseUp } = useModalOverlay(close)

const resetState = () => {
  invalidateRequests()
  step.value = 1
  errorMessage.value = ''
  form.email = ''
  form.password = ''
  form.passwordConfirm = ''
  recoveryToken.value = ''
  sessionNumber.value = ''
  codeReceiver.value = 'email'
  choiceChannels.value = []
  chosenChannel.value = undefined
  pendingCaptchaAction.value = null
  codeDigits.fill('')
  totpDigits.fill('')
  if (codeBurned.value) resetCodeLimit()
  captchaToken.value = ''
  captchaSession.value = ''
  captchaSessionExpiry.value = 0
  resendCooldown.value = 0
  if (cooldownIntervalId) {
    clearInterval(cooldownIntervalId)
    cooldownIntervalId = null
  }
}

// Watch for modelValue to pre-fill email when opening
watch(
  () => props.modelValue,
  (isOpen) => {
    if (!isOpen) resetState()
    if (isOpen && props.initialEmail) {
      form.email = props.initialEmail
    }
  },
  { immediate: true }
)

const goBack = (targetStep: 1 | 2 | 3) => {
  errorMessage.value = ''
  step.value = targetStep
  if (targetStep === 1) {
    invalidateRequests()
    pendingCaptchaAction.value = null
    captchaSessionExpiry.value = 0
    receiverAddress.value = ''
    recoveryToken.value = ''
    sessionNumber.value = ''
    captchaRef.value?.reset()
    if (cooldownIntervalId) {
      clearInterval(cooldownIntervalId)
      cooldownIntervalId = null
    }
    choiceChannels.value = []
    chosenChannel.value = undefined
    captchaSession.value = ''
    captchaToken.value = ''
    resendCooldown.value = 0
    codeDigits.fill('')
    totpDigits.fill('')
    // Сожжённый код остаётся в прошлом: следующий шаг всё равно пришлёт новый.
    if (codeBurned.value) resetCodeLimit()
  }
}

watch(() => form.email, () => goBack(1), { flush: 'sync' })

const switchToLogin = () => {
  close()
  emit('switch-to-login')
}

// Step 1: Trigger captcha popup, then send code
const handleSendCode = async () => {
  if (!props.modelValue || !form.email || isLoading.value || isResending.value || pendingCaptchaAction.value) return
  errorMessage.value = ''
  
  // Set pending action and trigger captcha popup (bind mode)
  pendingCaptchaAction.value = 'send_code'
  captchaRef.value?.showCaptcha()
}

const takeTelegramFailure = (error: unknown): boolean => {
  if (!isTelegramDeliveryFailure(error)) return false
  telegramRecovery.value = parseTelegramRecovery(error)
  captchaSession.value = telegramRecovery.value?.captchaSession ?? ''
  captchaSessionExpiry.value = Date.now() + 2 * 60 * 1000
  captchaToken.value = ''
  pendingCaptchaAction.value = null
  choiceChannels.value = []
  sessionNumber.value = ''
  codeDigits.fill('')
  errorMessage.value = ''
  resendCooldown.value = 0
  if (cooldownIntervalId) {
    clearInterval(cooldownIntervalId)
    cooldownIntervalId = null
  }
  step.value = 'delivery-recovery'
  return true
}

const retryTelegramDelivery = async (channel: OtpChoiceChannel) => {
  if (!props.modelValue || isLoading.value || isResending.value || codeThrottled.value
    || step.value !== 'delivery-recovery' || !telegramRecovery.value?.channels.includes(channel)) return
  chosenChannel.value = channel
  await performSendCode(channel)
}

/** Фазу выбора ловим до перехода на ввод кода: кода пока не существует. */
const takeChoice = (result: { choose?: OtpChoice }): boolean => {
  telegramRecovery.value = null
  if (!result.choose) return false
  if (step.value === 'delivery-recovery') step.value = 1

  choiceChannels.value = result.choose.channels
  // Отсутствующая свежая сессия не отменяет выбор: капчу попросим после клика.
  captchaSession.value = result.choose.captcha_session ?? ''
  captchaToken.value = ''

  return true
}

/** Человек выбрал канал — повторяем тот же запрос, теперь с ответом. */
const handleChoicePick = async (channel: OtpChoiceChannel): Promise<void> => {
  if (isLoading.value || isResending.value || !choiceChannels.value.includes(channel)) return
  chosenChannel.value = channel
  await performSendCode(channel)
}

// Called after captcha verification - performs actual send code
const performSendCode = async (channel = chosenChannel.value) => {
  if (!props.modelValue || isLoading.value || isResending.value) return
  const attempt = generation
  const email = form.email
  const isCurrent = () => attempt === generation
  isLoading.value = true
  errorMessage.value = ''

  try {

    const options = {
      captchaSession: captchaSession.value || undefined,
      recaptchaToken: captchaSession.value ? undefined : captchaToken.value || await requestCaptchaToken(),
      channel
    }
    if (attempt !== generation) return
    captchaSession.value = ''
    captchaToken.value = ''
    const result = await ProfileApi.startPasswordRecovery(email, options, isCurrent)
    if (attempt !== generation) return

    // Спринт 14: код не отправлен — сперва спрашиваем, куда его прислать
    if (takeChoice(result)) return

    choiceChannels.value = []
    captchaSession.value = result.captchaSession
    captchaSessionExpiry.value = Date.now() + 2 * 60 * 1000 // 2 minutes
    
    sessionNumber.value = result.session || ''
    codeReceiver.value = result.reciever || 'email'
    receiverAddress.value = result.reciever_address || form.email
    step.value = 2
    codeDigits.fill('')
    resetCodeLimit()
    startResendCooldown() // Start 60s cooldown
    nextTick(() => codeInputs.value[0]?.focus())
  } catch (error: any) {
    if (attempt !== generation || takeTelegramFailure(error)) return
    if (isCaptchaSessionExpiredError(error)) {
      try {
        const freshToken = await requestCaptchaToken()
        if (attempt !== generation) return
        const retryResult = await ProfileApi.startPasswordRecovery(email, {
          recaptchaToken: freshToken,
          channel
        }, isCurrent)
        if (attempt !== generation) return

        // Спринт 14: код не отправлен — сперва спрашиваем, куда его прислать
        if (takeChoice(retryResult)) return

        choiceChannels.value = []
        captchaSession.value = retryResult.captchaSession
        captchaSessionExpiry.value = Date.now() + 2 * 60 * 1000
        
        sessionNumber.value = retryResult.session || ''
        codeReceiver.value = retryResult.reciever || 'email'
        receiverAddress.value = retryResult.reciever_address || form.email
        step.value = 2
        codeDigits.fill('')
        resetCodeLimit()
        startResendCooldown() // Start 60s cooldown
        nextTick(() => codeInputs.value[0]?.focus())
        return
      } catch (retryError: any) {
        if (attempt !== generation || takeTelegramFailure(retryError)) return
        errorMessage.value = applyCodeLimit(retryError) ?? getErrorMessage(retryError)
      }
    } else {
      errorMessage.value = applyCodeLimit(error) ?? getErrorMessage(error)
    }
    captchaRef.value?.reset()
  } finally {
    if (attempt === generation) isLoading.value = false
  }
}

// Resend code (from step 2)
const handleResendCode = async () => {
  if (isResending.value || resendCooldown.value > 0 || codeThrottled.value) return
  const attempt = generation
  const email = form.email
  const isCurrent = () => attempt === generation
  const channel = chosenChannel.value
  
  isResending.value = true
  errorMessage.value = ''
  
  try {
    const session = captchaSession.value && Date.now() < captchaSessionExpiry.value
      ? captchaSession.value : undefined
    const options = {
      captchaSession: session,
      recaptchaToken: session ? undefined : await requestCaptchaToken(),
      channel
    }
    if (attempt !== generation) return
    // Captcha proof is single-use, including requests that fail after validation.
    captchaSession.value = ''
    captchaToken.value = ''
    const result = await ProfileApi.startPasswordRecovery(email, options, isCurrent)
    if (attempt !== generation) return

    // Спринт 14: код не отправлен — сперва спрашиваем, куда его прислать
    if (takeChoice(result)) return

    captchaSession.value = result.captchaSession
    captchaSessionExpiry.value = Date.now() + 2 * 60 * 1000
    
    sessionNumber.value = result.session || ''
    codeReceiver.value = result.reciever || 'email'
    receiverAddress.value = result.reciever_address || form.email
    codeDigits.fill('')
    resetCodeLimit()
    startResendCooldown()
    nextTick(() => codeInputs.value[0]?.focus())
  } catch (error: any) {
    if (attempt !== generation || takeTelegramFailure(error)) return
    if (isCaptchaSessionExpiredError(error)) {
      try {
        const freshToken = await requestCaptchaToken()
        if (attempt !== generation) return
        const retryResult = await ProfileApi.startPasswordRecovery(email, {
          recaptchaToken: freshToken,
          channel
        }, isCurrent)
        if (attempt !== generation) return

        // Спринт 14: код не отправлен — сперва спрашиваем, куда его прислать
        if (takeChoice(retryResult)) return

        captchaSession.value = retryResult.captchaSession
        captchaSessionExpiry.value = Date.now() + 2 * 60 * 1000
        
        sessionNumber.value = retryResult.session || ''
        codeReceiver.value = retryResult.reciever || 'email'
        receiverAddress.value = retryResult.reciever_address || form.email
        codeDigits.fill('')
        resetCodeLimit()
        startResendCooldown()
        nextTick(() => codeInputs.value[0]?.focus())
      } catch (retryError: any) {
        if (attempt !== generation || takeTelegramFailure(retryError)) return
        errorMessage.value = applyCodeLimit(retryError) ?? getErrorMessage(retryError)
      }
    } else {
      errorMessage.value = applyCodeLimit(error) ?? getErrorMessage(error)
    }
  } finally {
    if (attempt === generation) isResending.value = false
  }
}

/** 32: сожжённый код не оживить — сразу просим новый, без ожидания обычной паузы. */
const requestNewCode = async () => {
  resendCooldown.value = 0
  if (cooldownIntervalId) {
    clearInterval(cooldownIntervalId)
    cooldownIntervalId = null
  }
  await handleResendCode()
}

// Code input handlers
const handleCodeInput = (event: Event, index: number) => {
  const input = event.target as HTMLInputElement
  const val = input.value

  // Ensure only numbers
  if (!/^\d*$/.test(val)) {
    codeDigits[index] = ''
    return
  }

  if (val.length === 1 && index < 5) {
    codeInputs.value[index + 1]?.focus()
  } else if (val.length > 1) {
    codeDigits[index] = val.slice(-1)
  }
}

const handleCodeDelete = (index: number) => {
  if (codeDigits[index]) {
    codeDigits[index] = ''
    return
  }
  if (index > 0) {
    codeDigits[index - 1] = ''
    codeInputs.value[index - 1]?.focus()
  }
}

const handleCodePaste = (event: ClipboardEvent) => {
  const paste = event.clipboardData?.getData('text')
  if (!paste) return
  
  const numbers = paste.match(/\d/g)
  if (numbers) {
    numbers.slice(0, 6).forEach((num, i) => {
      codeDigits[i] = num
    })
    codeInputs.value[Math.min(numbers.length, 5)]?.focus()
    
    // Auto-submit if all 6 fields are filled after paste
    if (numbers.length >= 6) {
      const attempt = generation
      nextTick(() => {
        if (attempt === generation) handleConfirmCode()
      })
    }
  }
}

// Step 2: Confirm code and get token
const handleConfirmCode = async () => {
  if (!props.modelValue || step.value !== 2 || isLoading.value || !isCodeComplete.value || codeBurned.value) return

  const attempt = generation
  const pending = { token: null as string | null }
  pendingRecovery = pending
  const isCurrent = () => attempt === generation && pendingRecovery === pending
  isLoading.value = true
  errorMessage.value = ''

  try {
    const result = await ProfileApi.verifyRecoveryCode(form.email, getCodeValue(), isCurrent, (token) => {
      if (isCurrent()) pending.token = token
    })
    assertCurrentAuthRequest(isCurrent)

    // OTP-R6c: код из письма — только первый фактор, pending-токен не вход.
    if (result.requires_approval) {
      if (!pending.token && result.token) {
        pending.token = result.token
        httpClient.setToken(result.token)
      }
      if (result.approval_type !== 'totp') throw new Error('Unsupported recovery approval')
      totpDigits.fill('')
      step.value = 'totp'
      nextTick(() => totpInputs.value[0]?.focus())
      return
    }

    if (result.token) {
      recoveryToken.value = result.token
      step.value = 3
    } else {
      throw new Error('Token not received')
    }
  } catch (error: any) {
    if (!isCurrent() || error instanceof AuthRequestCancelled) return
    // Незаконченная попытка не оставляет после себя pending-токен.
    if (pending.token && httpClient.getToken() === pending.token) httpClient.clearToken()
    pending.token = null
    const limitMessage = applyCodeLimit(error)
    if (codeBurned.value) codeDigits.fill('')
    errorMessage.value = limitMessage ?? getErrorMessage(error)
  } finally {
    if (isCurrent()) isLoading.value = false
  }
}

const handleTotpInput = (event: Event, index: number) => {
  const val = (event.target as HTMLInputElement).value
  if (!/^\d*$/.test(val)) {
    totpDigits[index] = ''
    return
  }
  if (val.length > 1) totpDigits[index] = val.slice(-1)
  if (val && index < 5) totpInputs.value[index + 1]?.focus()
}

const handleTotpPaste = (event: ClipboardEvent) => {
  const numbers = event.clipboardData?.getData('text').match(/\d/g)
  if (!numbers) return
  numbers.slice(0, 6).forEach((num, i) => { totpDigits[i] = num })
  if (numbers.length >= 6) {
    const attempt = generation
    nextTick(() => { if (attempt === generation) handleTotpSubmit() })
  }
}

// OTP-R6c: подтверждаем pending-вход кодом из приложения, затем — новый пароль.
const handleTotpSubmit = async () => {
  if (!props.modelValue || step.value !== 'totp' || isLoading.value || !isTotpComplete.value) return

  const attempt = generation
  const pending = pendingRecovery
  approvalRequestActive.value = true
  const isCurrent = () => approvalRequestActive.value && attempt === generation && pending === pendingRecovery
  isLoading.value = true
  errorMessage.value = ''

  try {
    await profileStore.confirmLoginApproval({ code: totpDigits.join('') }, isCurrent, {
      onPendingToken: (token) => { if (isCurrent() && pending) pending.token = token },
      // Подтверждённый токен больше не pending: отмена после approval его не удаляет.
      onApproved: () => { if (isCurrent() && pending) pending.token = null },
    })
    assertCurrentAuthRequest(isCurrent)
    recoveryToken.value = httpClient.getToken() ?? ''
    step.value = 3
  } catch (error: any) {
    if (!isCurrent() || error instanceof AuthRequestCancelled) return
    // 31 — приложение временно не принимает коды, pending-сессия жива.
    errorMessage.value = error?.error_code === 31 ? t('auth.totpTooManyAttempts') : getErrorMessage(error)
    totpDigits.fill('')
  } finally {
    if (isCurrent()) {
      isLoading.value = false
      approvalRequestActive.value = false
    }
  }
}

// Step 3: Change password
const handleChangePassword = async () => {
  if (!isPasswordValid.value) {
    errorMessage.value = t('auth.passwordTooWeak')
    return
  }

  if (form.password !== form.passwordConfirm) {
    errorMessage.value = t('auth.passwordsMismatch')
    return
  }

  const attempt = generation
  const isCurrent = () => attempt === generation
  // Снимок формы: повтор после капчи отправляет то же, что человек подтвердил.
  const request = {
    passwordnew: form.password,
    passwordretry: form.passwordConfirm,
    code: getCodeValue(),
    token: recoveryToken.value,
  }
  const complete = () => {
    // Success: Store token and reload the app
    const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken'
    localStorage.setItem(storageKey, request.token)

    close()
    window.location.reload()
  }
  isLoading.value = true
  errorMessage.value = ''

  try {
    await ProfileApi.setNewPassword({ ...request, captchaSession: captchaSession.value || undefined }, isCurrent)
    assertCurrentAuthRequest(isCurrent)
    complete()
  } catch (error: any) {
    if (!isCurrent() || error instanceof AuthRequestCancelled) return
    if (isCaptchaSessionExpiredError(error)) {
      try {
        const freshToken = await requestCaptchaToken()
        assertCurrentAuthRequest(isCurrent)
        await ProfileApi.setNewPassword({ ...request, recaptchaToken: freshToken }, isCurrent)
        assertCurrentAuthRequest(isCurrent)
        complete()
        return
      } catch (retryError: any) {
        if (!isCurrent() || retryError instanceof AuthRequestCancelled) return
        errorMessage.value = getErrorMessage(retryError)
      }
    } else {
      errorMessage.value = getErrorMessage(error)
    }
  } finally {
    if (isCurrent()) isLoading.value = false
  }
}
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--ds-scrim);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  // Keep the SDK's sibling captcha layer out of backdrop-filter compositing.
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
}

.modal-overlay--inline {
  position: relative;
  inset: auto;
  width: 100%;
  height: auto;
  display: block;
  padding: 0;
  background: transparent;
  backdrop-filter: none;
  .modal-content {
    margin: 0 auto;
    max-height: none;
    overflow: visible;
  }
}

.modal-content {
  background: var(--ds-surface);
  border-radius: var(--ds-radius-modal);
  padding: 40px;
  width: 100%;
  max-width: 480px;
  position: relative;
  box-shadow: 0 20px 40px var(--ds-scrim);
  max-height: 90vh;
  overflow-y: auto;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  font-size: var(--ds-text-size-h-md);
  color: var(--ds-text-muted);
  cursor: pointer;
  
  &:hover {
    color: var(--ds-text-secondary);
  }
}

.modal-title {
  font-family: var(--ds-font-display);
  font-size: var(--p-text-28);
  letter-spacing: -0.02em;
  font-weight: var(--ds-weight-bold);
  text-align: center;
  margin-bottom: 16px;
  color: var(--ds-text);
}

.modal-subtitle {
  text-align: center;
  color: var(--ds-text-secondary);
  margin-bottom: 24px;
  line-height: 1.5;

  strong {
    color: var(--ds-text);
  }
}

.success-icon {
  text-align: center;
  margin-bottom: 16px;

  i {
    font-size: var(--p-text-48);
    color: var(--ds-accent);
  }
}

.form-group {
  margin-bottom: 20px;
}

.session-number {
  text-align: center;
  font-weight: var(--ds-weight-semibold);
  color: var(--ds-accent);
  margin-bottom: 12px;
  font-size: var(--ds-text-size-body);
  background: var(--ds-accent-soft-05);
  padding: 8px;
  border-radius: var(--ds-radius-control);
}

.form-label {
  display: block;
  font-weight: var(--ds-weight-semibold);
  margin-bottom: 8px;
  color: var(--ds-text);
}

.form-input {
  width: 100%;
  padding: 12px 18px;
  border: 1px solid var(--ds-border);
  border-radius: var(--p-radius-50);
  background: var(--ds-overlay-04);
  color: var(--ds-text);
  font-size: var(--ds-text-size-body-lg);
  transition: border-color 0.2s, box-shadow 0.2s;

  &::placeholder {
    color: var(--ds-text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--ds-accent);
    box-shadow: var(--ds-focus-ring-shadow);
  }
}

.code-digits {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.code-digit {
  width: 48px;
  height: 56px;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-card);
  text-align: center;
  font-size: var(--ds-text-size-h-md);
  font-weight: var(--ds-weight-semibold);
  color: var(--ds-text);
  background: var(--ds-surface-canvas);
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--ds-accent);
    background: var(--ds-surface);
    box-shadow: 0 0 0 4px var(--ds-accent-soft-15);
  }
}

.password-input-wrapper {
  position: relative;
  
  .toggle-password {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--ds-text-muted);
    cursor: pointer;
    padding: 4px;
    
    &:hover {
      color: var(--ds-text-secondary);
    }
  }

  .form-input {
    padding-right: 44px;
  }
}

.password-requirements {
  background: var(--ds-surface-raised);
  border-radius: var(--ds-radius-card);
  padding: 16px;
  margin-bottom: 24px;

  .requirement {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: var(--p-text-13);
    color: var(--ds-text-muted);
    margin-bottom: 6px;

    &:last-child {
      margin-bottom: 0;
    }

    i {
      font-size: var(--ds-text-size-body);
    }

    &.met {
      color: var(--ds-success);
    }
  }
}

.form-error {
  margin-bottom: 16px;
  color: #d93025;
  font-size: var(--ds-text-size-body);
  line-height: 1.4;
}

.field-error {
  margin-top: 6px;
  margin-bottom: 0;
  color: #d93025;
  font-size: var(--p-text-13);
}

.input-error {
  border-color: #d93025 !important;
}

.step-actions {
  display: flex;
  gap: 12px;

  .btn {
    flex: 1;
  }
}

.btn {
  padding: 14px;
  border-radius: var(--p-radius-50);
  font-weight: var(--ds-weight-semibold);
  font-size: var(--ds-text-size-body-lg);
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-block {
  width: 100%;
  display: block;
}

.btn-primary {
  background: var(--ds-accent);
  color: var(--ds-surface);
  
  &:hover:not(:disabled) {
    background: var(--ds-accent-strong);
  }
}

.btn-outline {
  background: transparent;
  border: 1px solid var(--ds-border);
  color: var(--ds-text);

  &:hover:not(:disabled) {
    background: var(--ds-surface-raised);
  }
}

.resend-link {
  text-align: center;
  margin-bottom: 16px;

  a {
    color: var(--ds-accent);
    text-decoration: none;
    font-weight: var(--ds-weight-semibold);
    font-size: var(--ds-text-size-body);

    &.disabled {
      color: var(--ds-text-muted);
      pointer-events: none;
    }
  }
}

.auth-footer {
  text-align: center;
  margin-top: 24px;
  color: var(--ds-text-secondary);
  
  a {
    color: var(--ds-accent);
    text-decoration: none;
    font-weight: var(--ds-weight-semibold);
  }
}

// Hidden captcha container (bind mode - popup triggered programmatically)
.captcha-hidden-container {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  visibility: hidden;
}
</style>
